/**
 * Placeholders shown while a screen's data is in flight.
 *
 * Why a skeleton rather than a spinner: these pages are tables whose shape is known
 * before the data arrives, so showing that shape means the layout never moves when the
 * rows land. A spinner in the middle of an empty page tells you to wait and then throws
 * the content at you from a different position.
 *
 * `aria-hidden` and a live region instead: a screen reader should hear "loading users",
 * not a description of eight grey rectangles.
 */

export function SkeletonRows({
  rows = 6,
  columns,
  label,
}: {
  rows?: number;
  /** Relative widths, so the placeholder matches the real table's rhythm. */
  columns: number[];
  label: string;
}) {
  return (
    <>
      <span className="sr-only" role="status" aria-live="polite">
        {label}
      </span>
      <div className="skel-table" aria-hidden="true">
        {Array.from({ length: rows }, (_, r) => (
          <div className="skel-row" key={r}>
            {columns.map((flex, c) => (
              <span className="skel" style={{ flex }} key={c} />
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

/** A single block, for a panel whose shape is not a table. */
export function SkeletonBlock({ height = 80, label }: { height?: number; label?: string }) {
  return (
    <>
      {label && (
        <span className="sr-only" role="status" aria-live="polite">
          {label}
        </span>
      )}
      <span className="skel" style={{ height, display: "block" }} aria-hidden="true" />
    </>
  );
}
